import React from 'react';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Calendar } from 'primereact/calendar';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import acordLogo from 'images/acordlogo.png';
import CreateNewCarriers from '../CreateNewCarriers';
import AddNewSignature from '../AddNewSignature';
import { useSignatures } from '../../../../hooks/useSignatures';
import ProducerSection, { fetchPolicyAgentContact } from '../ProducerSection';
import InsurerSearchDropdown from '../InsurerSearchDropdown';
import { formatUsPhone } from 'utilities/formatPhone';

const INSR_OPTIONS = ['A','B','C','D','E'].map(l => ({ label: l, value: l }));

interface PolicyData {
  policyNumber: string;
  effDate: Date | null;
  expDate: Date | null;
  insLtr: string;
  nfipWyo: boolean;
  privateAlt: boolean;
  dwellingForm: boolean;
  generalPropertyForm: boolean;
  resCondo: boolean;
  standardPolicy: boolean;
  preferredRisk: boolean;
  prefRiskEligibility: boolean;
  groupFlood: boolean;
  mortgagePortfolio: boolean;
}
const emptyPolicy = (): PolicyData => ({
  policyNumber: '', effDate: null, expDate: null, insLtr: '',
  nfipWyo: false, privateAlt: false, dwellingForm: false,
  generalPropertyForm: false, resCondo: false, standardPolicy: false,
  preferredRisk: false, prefRiskEligibility: false, groupFlood: false,
  mortgagePortfolio: false,
});

interface BenefitsData {
  businessIncome: boolean;
  extraExpense: boolean;
  additionalLiving: boolean;
  ifYesLimit: string;
  actualLossSustained: boolean;
  numMonths: string;
}
const emptyBenefits = (): BenefitsData => ({
  businessIncome: false, extraExpense: false, additionalLiving: false,
  ifYesLimit: '', actualLossSustained: false, numMonths: '',
});

interface Acord29FormProps { selectedPolicies?: string[]; insuredId?: string; }

export default function Acord29Form({ selectedPolicies = [], insuredId = '' }: Acord29FormProps) {
  const [showCreateCarrier, setShowCreateCarrier] = React.useState(false);
  const [showAddSignature, setShowAddSignature] = React.useState(false);
  const [certDate, setCertDate] = React.useState<Date | null>(new Date());
  const [producerAddress, setProducerAddress] = React.useState('');
  const [producerCity, setProducerCity] = React.useState('');
  const [producerState, setProducerState] = React.useState('');
  const [producerZip, setProducerZip] = React.useState('');
  const [producerStreet, setProducerStreet] = React.useState('');

  const [contactName, setContactName] = React.useState('');
  const [contactPhone, setContactPhone] = React.useState('');
  const [contactFax, setContactFax] = React.useState('');
  const [contactEmail, setContactEmail] = React.useState('');
  const [producerCustomerId, setProducerCustomerId] = React.useState('');

  const [insuredName, setInsuredName] = React.useState('');
  const [insuredAddress, setInsuredAddress] = React.useState('');
  const [insuredCity, setInsuredCity] = React.useState('');
  const [insuredState, setInsuredState] = React.useState('');
  const [insuredZip, setInsuredZip] = React.useState('');

  const [insurerA, setInsurerA] = React.useState('');
  const [insurerB, setInsurerB] = React.useState('');
  const [insurerC, setInsurerC] = React.useState('');
  const [insurerD, setInsurerD] = React.useState('');
  const [insurerE, setInsurerE] = React.useState('');

  const [evidenceNumber, setEvidenceNumber] = React.useState('');
  const [revisionNumber, setRevisionNumber] = React.useState('');
  const [pageCount, setPageCount] = React.useState('');
  const [replacesPriorDate, setReplacesPriorDate] = React.useState<Date | null>(null);

  const [dateOfConstruction, setDateOfConstruction] = React.useState<Date | null>(null);
  const [currentFloodZone, setCurrentFloodZone] = React.useState('');
  const [floodRiskZone, setFloodRiskZone] = React.useState('');
  const [grandFathered, setGrandFathered] = React.useState('');

  const [occSingleFamily, setOccSingleFamily] = React.useState(false);
  const [occOtherResidential, setOccOtherResidential] = React.useState(false);
  const [occTwoFourFamily, setOccTwoFourFamily] = React.useState(false);
  const [occNonResidential, setOccNonResidential] = React.useState(false);
  const [contResidential, setContResidential] = React.useState(false);
  const [contNonResidential, setContNonResidential] = React.useState(false);

  const [replacementCost, setReplacementCost] = React.useState('');
  const [condoUnitOwner, setCondoUnitOwner] = React.useState(false);
  const [condoAssocBuilding, setCondoAssocBuilding] = React.useState(false);
  const [numUnits, setNumUnits] = React.useState('');

  const [primaryPolicy, setPrimaryPolicy] = React.useState<PolicyData>(emptyPolicy());
  const [excessPolicy1, setExcessPolicy1] = React.useState<PolicyData>(emptyPolicy());
  const [excessPolicy2, setExcessPolicy2] = React.useState<PolicyData>(emptyPolicy());
  const [excess1FollowingForm, setExcess1FollowingForm] = React.useState(false);
  const [excess2FollowingForm, setExcess2FollowingForm] = React.useState(false);

  const [benefits1, setBenefits1] = React.useState<BenefitsData>(emptyBenefits());
  const [benefits2, setBenefits2] = React.useState<BenefitsData>(emptyBenefits());

  const [remarks, setRemarks] = React.useState('');
  const [loanNumber, setLoanNumber] = React.useState('');
  const [aiName, setAiName] = React.useState('');
  const [aiAddress1, setAiAddress1] = React.useState('');
  const [aiAddress2, setAiAddress2] = React.useState('');
  const [aiCity, setAiCity] = React.useState('');
  const [aiState, setAiState] = React.useState('');
  const [aiZip, setAiZip] = React.useState('');

  const [aiAdditionalInsured, setAiAdditionalInsured] = React.useState(false);
  const [aiMortgagee, setAiMortgagee] = React.useState(false);
  const [aiLendersLossPayable, setAiLendersLossPayable] = React.useState(false);
  const [aiUnitOwnerMortgagee, setAiUnitOwnerMortgagee] = React.useState(false);
  const [aiLossPayee, setAiLossPayee] = React.useState(false);
  const [namedPrimary, setNamedPrimary] = React.useState(false);
  const [namedExcess1, setNamedExcess1] = React.useState(false);
  const [namedExcess2, setNamedExcess2] = React.useState(false);

  const [authRepSignature, setAuthRepSignature] = React.useState('');
  const { signatureOptions, refetchSignatures } = useSignatures();
  const [signaturePreviewUrl, setSignaturePreviewUrl] = React.useState('');
  React.useEffect(() => {
    if (!authRepSignature) { setSignaturePreviewUrl(''); return; }
    fetch(`${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/signature-preview/${authRepSignature}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('igtoken') || ''}`,
        'Content-Type': 'application/json',
        NCToken: localStorage.getItem('NCToken') || '',
      },
    })
      .then(r => r.ok ? r.text() : Promise.reject())
      .then(url => setSignaturePreviewUrl(url.replace(/^"|"$/g, '')))
      .catch(() => setSignaturePreviewUrl(''));
  }, [authRepSignature]);

  React.useEffect(() => {
    if (!selectedPolicies || selectedPolicies.length === 0) return;
    let cancelled = false;
    const fetchCoverages = async () => {
      try {
        const ncToken = localStorage.getItem('NCToken') || '';
        const igToken = localStorage.getItem('igtoken') || '';
        const res = await fetch(
          `${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/get-coverages`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'NCToken': ncToken,
              Authorization: `Bearer ${igToken}`,
            },
            body: JSON.stringify({
              policiesIds: selectedPolicies,
              truckingCompanyId: insuredId || '',
              fileParentId: 'B7972059-51D0-4243-A7DE-724D564557DA',
              naics: [{ Code: '', Name: '' }],
              Page: 'certificateFiller',
            }),
          }
        );
        if (cancelled) return;
        await res.json();
      } catch {
        // silently fail — user can still fill manually
      }
    };
    fetchCoverages();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPolicies, insuredId]);

  // Prefill contact section from the first selected policy's agent
  React.useEffect(() => {
    if (!selectedPolicies || selectedPolicies.length === 0) return;
    fetchPolicyAgentContact(selectedPolicies[0]).then(({ name, phone, fax, email }) => {
      if (name) setContactName(name);
      if (phone) setContactPhone(phone);
      if (fax) setContactFax(fax);
      if (email) setContactEmail(email);
    });
  }, [selectedPolicies]);

  const chk = (id: string, label: string, checked: boolean, onChange: (v: boolean) => void) => (
    <div className="masterCert__checkboxItem">
      <Checkbox inputId={id} checked={checked} onChange={(e) => onChange(e.checked ?? false)} />
      <label htmlFor={id} className="masterCert__checkboxItemLabel">{label}</label>
    </div>
  );

  const updatePolicy = (
    setter: React.Dispatch<React.SetStateAction<PolicyData>>,
    field: keyof PolicyData,
    value: any,
  ) => {
    setter(prev => ({ ...prev, [field]: value }));
  };

  const updateBenefits = (
    setter: React.Dispatch<React.SetStateAction<BenefitsData>>,
    field: keyof BenefitsData,
    value: any,
  ) => {
    setter(prev => ({ ...prev, [field]: value }));
  };

  const renderPolicySection = (
    policy: PolicyData,
    setPolicy: React.Dispatch<React.SetStateAction<PolicyData>>,
    prefix: string,
  ) => (
    <>
      <div className="acord29__policyInfoRow">
        <span className="masterCert__contactLabel">POLICY NUMBER:</span>
        <InputText value={policy.policyNumber} onChange={(e) => updatePolicy(setPolicy, 'policyNumber', e.target.value)} className="masterCert__input" style={{ width: 180 }} />
        <span className="masterCert__contactLabel">* EFFECTIVE DATE:</span>
        <Calendar value={policy.effDate} onChange={(e) => updatePolicy(setPolicy, 'effDate', e.value)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
        <span className="masterCert__contactLabel">* EXPIRATION DATE:</span>
        <Calendar value={policy.expDate} onChange={(e) => updatePolicy(setPolicy, 'expDate', e.value)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
      </div>

      <div className="acord29__covBlock">
        <div className="acord29__covLeft">
          <div className="acord29__covLeftHeader">
            <div>INS<br/>LTR</div>
            <div>COVERAGE</div>
            <div>DEDUCTIBLE</div>
            <div>TOTAL AMOUNT OF<br/>INSURANCE</div>
          </div>
          <div className="acord29__covLeftRow">
            <Dropdown value={policy.insLtr} onChange={(e) => updatePolicy(setPolicy, 'insLtr', e.value)} options={INSR_OPTIONS} placeholder="" className="masterCert__input masterCert__inputFull" emptyMessage="No available options" />
            <div className="acord29__covLeftLabel">BUILDING</div>
            <InputText className="masterCert__input masterCert__inputFull" />
            <InputText className="masterCert__input masterCert__inputFull" />
          </div>
          <div className="acord29__covLeftRow">
            <div />
            <div className="acord29__covLeftLabel">CONTENTS</div>
            <InputText className="masterCert__input masterCert__inputFull" />
            <InputText className="masterCert__input masterCert__inputFull" />
          </div>
        </div>

        <div className="acord29__covRight">
          <div className="acord29__covGroup">
            <div className="acord29__covGroupTitle">MARKET</div>
            {chk(`${prefix}NfipWyo`, 'NFIP / WYO', policy.nfipWyo, (v) => updatePolicy(setPolicy, 'nfipWyo', v))}
            {chk(`${prefix}PrivAlt`, 'PRIVATE / ALT. MARKET', policy.privateAlt, (v) => updatePolicy(setPolicy, 'privateAlt', v))}
          </div>
          <div className="acord29__covGroup">
            <div className="acord29__covGroupTitle">POLICY FORM</div>
            {chk(`${prefix}Dwelling`, 'DWELLING FORM', policy.dwellingForm, (v) => updatePolicy(setPolicy, 'dwellingForm', v))}
            {chk(`${prefix}GenProp`, 'GENERAL PROPERTY FORM', policy.generalPropertyForm, (v) => updatePolicy(setPolicy, 'generalPropertyForm', v))}
            {chk(`${prefix}ResCondo`, 'RESIDENTIAL CONDO BLDG ASSOCIATION POLICY FORM', policy.resCondo, (v) => updatePolicy(setPolicy, 'resCondo', v))}
          </div>
          <div className="acord29__covGroup">
            <div className="acord29__covGroupTitle">PRODUCT TYPE</div>
            {chk(`${prefix}StdPolicy`, 'STANDARD POLICY', policy.standardPolicy, (v) => updatePolicy(setPolicy, 'standardPolicy', v))}
            {chk(`${prefix}PrefRisk`, 'PREFERRED RISK POLICY', policy.preferredRisk, (v) => updatePolicy(setPolicy, 'preferredRisk', v))}
            <Checkbox checked={false} />
          </div>
          <div className="acord29__covGroup">
            <div className="acord29__covGroupTitle">&nbsp;</div>
            {chk(`${prefix}PrefExt`, 'PREFERRED RISK POLICY ELIGIBILITY EXTENSION', policy.prefRiskEligibility, (v) => updatePolicy(setPolicy, 'prefRiskEligibility', v))}
            {chk(`${prefix}GroupFlood`, 'GROUP FLOOD INSURANCE POLICY', policy.groupFlood, (v) => updatePolicy(setPolicy, 'groupFlood', v))}
            {chk(`${prefix}MortPortfolio`, 'MORTGAGE PORTFOLIO PROTECTION PROGRAM POLICY', policy.mortgagePortfolio, (v) => updatePolicy(setPolicy, 'mortgagePortfolio', v))}
          </div>
        </div>
      </div>
    </>
  );

  const renderBenefitsRow = (
    benefits: BenefitsData,
    setBenefits: React.Dispatch<React.SetStateAction<BenefitsData>>,
    prefix: string,
  ) => (
    <div className="acord29__benefitsRow">
      {chk(`${prefix}BusIncome`, 'BUSINESS INCOME', benefits.businessIncome, (v) => updateBenefits(setBenefits, 'businessIncome', v))}
      {chk(`${prefix}ExtraExp`, 'EXTRA EXPENSE', benefits.extraExpense, (v) => updateBenefits(setBenefits, 'extraExpense', v))}
      {chk(`${prefix}AddlLiving`, 'ADDITIONAL LIVING EXPENSE', benefits.additionalLiving, (v) => updateBenefits(setBenefits, 'additionalLiving', v))}
      <span className="masterCert__contactLabel" style={{ whiteSpace: 'nowrap' }}>If "YES", LIMIT: $</span>
      <InputText value={benefits.ifYesLimit} onChange={(e) => updateBenefits(setBenefits, 'ifYesLimit', e.target.value)} className="masterCert__input" style={{ width: 80 }} />
      {chk(`${prefix}ActualLoss`, 'ACTUAL LOSS SUSTAINED', benefits.actualLossSustained, (v) => updateBenefits(setBenefits, 'actualLossSustained', v))}
      <span className="masterCert__contactLabel" style={{ whiteSpace: 'nowrap' }}># OF MONTHS:</span>
      <InputText value={benefits.numMonths} onChange={(e) => updateBenefits(setBenefits, 'numMonths', e.target.value)} className="masterCert__input" style={{ width: 50 }} />
    </div>
  );

  return (
    <>
      <div className="masterCert__section">
        <div className="masterCert__acordHeader">
          <img src={acordLogo} alt="ACORD" className="masterCert__acordLogo" />
          <div className="masterCert__acordTitleWrapper">
            <div className="masterCert__acordTitle">EVIDENCE OF FLOOD INSURANCE</div>
          </div>
          <div className="masterCert__dateWrapper">
            <div className="masterCert__dateLabel">DATE (MM/DD/YYYY)</div>
            <div className="masterCert__dateCell">
              <Calendar value={certDate} onChange={(e) => setCertDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
          </div>
        </div>

        <div className="masterCert__disclaimer">
          This evidence of flood insurance is issued as a matter of information only and confers no rights upon the additional interest named below. This evidence does not affirmatively or negatively amend, extend or alter the coverage afforded by the policies below. This evidence of insurance does not constitute a contract between the issuing insurer(s), authorized representative or producer, and the additional interest.
        </div>

        <div className="masterCert__twoColumnLayout">
          <div>
            <ProducerSection
              title="INSURANCE AGENT / PRODUCER"
              address={producerAddress}
              onAddressChange={setProducerAddress}
              city={producerCity}
              onCityChange={setProducerCity}
              street={producerStreet}
              onStreetChange={setProducerStreet}
              state={producerState}
              onStateChange={setProducerState}
              zip={producerZip}
              onZipChange={setProducerZip}
              onAgentContact={(phone, fax, email) => { setContactPhone(phone); setContactFax(fax); setContactEmail(email); }}
              readOnly
            />
            <div>
              <div className="masterCert__sectionHeader">NAMED INSURED AND ADDRESS</div>
              <Dropdown value={insuredName} onChange={(e) => setInsuredName(e.value)} options={[]} placeholder="Type and/or choose location" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" editable disabled emptyMessage="No available options" />
              <InputText maxLength={100} value={insuredAddress} onChange={(e) => setInsuredAddress(e.target.value)} placeholder="Address" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" readOnly />
              <InputText className="masterCert__input masterCert__inputFull masterCert__inputMbSm" placeholder="Address line 2" />
              <div className="masterCert__addressGrid">
                <InputText maxLength={100} value={insuredCity} onChange={(e) => setInsuredCity(e.target.value)} placeholder="City" className="masterCert__input masterCert__inputFull" readOnly />
                <InputText maxLength={50} value={insuredState} onChange={(e) => setInsuredState(e.target.value)} placeholder="State" className="masterCert__input masterCert__inputFull" readOnly />
                <InputText maxLength={20} value={insuredZip} onChange={(e) => setInsuredZip(e.target.value)} placeholder="Zip" className="masterCert__input masterCert__inputFull" readOnly />
              </div>
            </div>
          </div>

          <div>
            <div style={{ marginBottom: 16 }}>
              <div className="masterCert__contactGrid">
                <div>
                  <div className="masterCert__contactLabel">CONTACT NAME:</div>
                  <InputText maxLength={100} value={contactName} onChange={(e) => setContactName(e.target.value)} className="masterCert__input masterCert__inputFull" readOnly />
                </div>
                <div></div>
                <div>
                  <div className="masterCert__contactLabel">PHONE (A/C, No, Ext):</div>
                  <InputText value={formatUsPhone(contactPhone)} onChange={(e) => setContactPhone(e.target.value)} className="masterCert__input masterCert__inputFull" readOnly />
                </div>
                <div>
                  <div className="masterCert__contactLabel">FAX (A/C, No):</div>
                  <InputText value={formatUsPhone(contactFax)} onChange={(e) => setContactFax(e.target.value)} className="masterCert__input masterCert__inputFull" readOnly />
                </div>
                <div className="masterCert__contactLabel">E-MAIL ADDRESS:</div>
                <InputText maxLength={100} value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} className="masterCert__input masterCert__inputFull" readOnly />
              </div>
              <div style={{ marginTop: 6 }}>
                <div className="masterCert__contactLabel">PRODUCER CUSTOMER ID #:</div>
                <InputText maxLength={30} value={producerCustomerId} onChange={(e) => setProducerCustomerId(e.target.value)} className="masterCert__input masterCert__inputFull" />
              </div>
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8, gap: 8 }}>
                <div className="masterCert__sectionHeader" style={{ marginBottom: 0 }}>INSURERS AFFORDING COVERAGE (and NAIC #)</div>
                <Button label="Add New" icon="pi pi-plus" outlined size="small" className="carrierDialog__secondaryBtn" style={{ flexShrink: 0, padding: '2px 10px', fontSize: '0.7rem', height: '28px' }} onClick={() => setShowCreateCarrier(true)} />
              </div>
              <div className="masterCert__insurersGrid">
                <div className="masterCert__insurerLabel">INSURER A</div>
                <InsurerSearchDropdown value={insurerA} onChange={setInsurerA} className="masterCert__input masterCert__inputFull" />
                <div className="masterCert__insurerLabel">INSURER B</div>
                <InsurerSearchDropdown value={insurerB} onChange={setInsurerB} className="masterCert__input masterCert__inputFull" />
                <div className="masterCert__insurerLabel">INSURER C</div>
                <InsurerSearchDropdown value={insurerC} onChange={setInsurerC} className="masterCert__input masterCert__inputFull" />
                <div className="masterCert__insurerLabel">INSURER D</div>
                <InsurerSearchDropdown value={insurerD} onChange={setInsurerD} className="masterCert__input masterCert__inputFull" />
                <div className="masterCert__insurerLabel">INSURER E</div>
                <InsurerSearchDropdown value={insurerE} onChange={setInsurerE} className="masterCert__input masterCert__inputFull" />
              </div>

              <div style={{ marginTop: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span className="masterCert__contactLabel">EVIDENCE NUMBER</span>
                  <InputText maxLength={30} value={evidenceNumber} onChange={(e) => setEvidenceNumber(e.target.value)} className="masterCert__input" style={{ flex: 1 }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span className="masterCert__contactLabel">REVISION NUMBER</span>
                  <InputText maxLength={30} value={revisionNumber} onChange={(e) => setRevisionNumber(e.target.value)} className="masterCert__input" style={{ flex: 1 }} />
                  <span className="masterCert__contactLabel">PAGE COUNT</span>
                  <InputText maxLength={30} value={pageCount} onChange={(e) => setPageCount(e.target.value)} className="masterCert__input" style={{ width: 50 }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="masterCert__contactLabel" style={{ whiteSpace: 'nowrap', fontStyle: 'italic' }}>THIS REPLACES PRIOR EVIDENCE DATED</span>
                  <Calendar value={replacesPriorDate} onChange={(e) => setReplacesPriorDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="acord20__sectionTitle">PROPERTY INFORMATION <span style={{ fontWeight: 400, fontSize: '0.75rem' }}>(Use REMARKS, if more space is required)</span></div>

        <div className="acord29__locationGrid">
          <div className="masterCert__contactLabel">LOCATION / DESCRIPTION</div>
          <InputText className="masterCert__input masterCert__inputFull" placeholder="Address" />
          <InputText className="masterCert__input masterCert__inputFull" placeholder="City" />
          <InputText className="masterCert__input masterCert__inputFull" placeholder="State" />
          <InputText className="masterCert__input masterCert__inputFull" placeholder="Zip" />
          <InputText className="masterCert__input masterCert__inputFull" placeholder="Description" />
        </div>
        <div className="acord29__locationRow2">
          <div />
          <InputText className="masterCert__input masterCert__inputFull" placeholder="Street" />
          <InputText className="masterCert__input masterCert__inputFull" placeholder="State" />
          <InputText className="masterCert__input masterCert__inputFull" placeholder="Zip" />
        </div>

        <div className="masterCert__disclaimer" style={{ marginBottom: 12 }}>
          The policies of insurance listed below have been issued to the insured named above for the policy period indicated. Notwithstanding any requirement, term or condition of any contract or other document with respect to which this evidence of property insurance may be issued or may pertain, the insurance afforded by the policies described herein is subject to all the terms, exclusions and conditions of such policies. Limits shown may have been reduced by paid claims.
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
          <div className="acord20__sectionTitle" style={{ margin: 0 }}>COVERAGE / RISK INFORMATION</div>
          <div className="acord29__riskNote">* 12:01 A.M. LOCAL TIME AT THE INSURED PROPERTY LOCATION</div>
        </div>

        <div className="acord29__riskGrid">
          <div>
            <div className="masterCert__contactLabel" style={{ marginBottom: 4 }}>DATE OF CONSTRUCTION</div>
            <Calendar value={dateOfConstruction} onChange={(e) => setDateOfConstruction(e.value as Date)} className="masterCert__input masterCert__inputFull" showIcon dateFormat="mm/dd/yy" />
          </div>
          <div>
            <div className="masterCert__contactLabel" style={{ marginBottom: 4 }}>CURRENT FLOOD ZONE</div>
            <InputText maxLength={100} value={currentFloodZone} onChange={(e) => setCurrentFloodZone(e.target.value)} className="masterCert__input masterCert__inputFull" />
          </div>
          <div>
            <div className="masterCert__contactLabel" style={{ marginBottom: 4 }}>FLOOD RISK / RATED ZONE</div>
            <InputText maxLength={100} value={floodRiskZone} onChange={(e) => setFloodRiskZone(e.target.value)} className="masterCert__input masterCert__inputFull" />
          </div>
          <div>
            <div className="masterCert__contactLabel" style={{ marginBottom: 4 }}>GRAND-FATHERED?<br/>Y/N</div>
            <InputText maxLength={100} value={grandFathered} onChange={(e) => setGrandFathered(e.target.value)} className="masterCert__input masterCert__inputFull" />
          </div>
        </div>

        <div className="acord29__occupancyGrid">
          <div>
            <div className="masterCert__contactLabel" style={{ marginBottom: 6 }}>BUILDING OCCUPANCY TYPE</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px' }}>
              {chk('a29OccSingle', 'SINGLE FAMILY', occSingleFamily, setOccSingleFamily)}
              {chk('a29OccOther', 'OTHER RESIDENTIAL', occOtherResidential, setOccOtherResidential)}
              {chk('a29Occ24', '2-4 FAMILY', occTwoFourFamily, setOccTwoFourFamily)}
              {chk('a29OccNonRes', 'NON-RESIDENTIAL', occNonResidential, setOccNonResidential)}
            </div>
          </div>
          <div>
            <div className="masterCert__contactLabel" style={{ marginBottom: 6 }}>CONTENTS COVERAGE TYPE</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px' }}>
              {chk('a29ContRes', 'RESIDENTIAL', contResidential, setContResidential)}
              {chk('a29ContNonRes', 'NON-RESIDENTIAL', contNonResidential, setContNonResidential)}
            </div>
          </div>
        </div>

        <div className="acord29__replacementRow">
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span className="masterCert__contactLabel">REPLACEMENT COST</span>
            <span style={{ fontSize: '0.75rem', color: '#555' }}>$</span>
            <InputText value={replacementCost} onChange={(e) => setReplacementCost(e.target.value)} className="masterCert__input" style={{ width: 80 }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="masterCert__contactLabel">CONDOMINIUM COVERAGE <span style={{ fontWeight: 400, fontStyle: 'italic' }}>(check one)</span></span>
            {chk('a29CondoUnit', 'UNIT OWNER', condoUnitOwner, setCondoUnitOwner)}
            {chk('a29CondoAssoc', 'ASSOCIATION BUILDING', condoAssocBuilding, setCondoAssocBuilding)}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span className="masterCert__contactLabel"># UNITS</span>
            <InputText maxLength={30} value={numUnits} onChange={(e) => setNumUnits(e.target.value)} className="masterCert__input" style={{ width: 50 }} />
          </div>
        </div>

        <div className="acord29__policyTitle">PRIMARY POLICY</div>
        {renderPolicySection(primaryPolicy, setPrimaryPolicy, 'pp')}

        <div className="acord29__excessHeader">
          <div className="acord29__policyTitle" style={{ margin: 0 }}>EXCESS POLICY 1</div>
          {chk('a29Excess1FF', "INDICATES EXCESS POLICY IS A 'FOLLOWING FORM' POLICY TYPE", excess1FollowingForm, setExcess1FollowingForm)}
        </div>
        {renderPolicySection(excessPolicy1, setExcessPolicy1, 'ep1')}
        {renderBenefitsRow(benefits1, setBenefits1, 'b1')}

        <div className="acord29__excessHeader">
          <div className="acord29__policyTitle" style={{ margin: 0 }}>EXCESS POLICY 2</div>
          {chk('a29Excess2FF', "INDICATES EXCESS POLICY IS A 'FOLLOWING FORM' POLICY TYPE", excess2FollowingForm, setExcess2FollowingForm)}
        </div>
        {renderPolicySection(excessPolicy2, setExcessPolicy2, 'ep2')}
        {renderBenefitsRow(benefits2, setBenefits2, 'b2')}

        <div className="masterCert__descriptionSection" style={{ marginTop: 16 }}>
          <div className="masterCert__descriptionTitle">REMARKS (ACORD 101, Additional Remarks Schedule, may be attached if more space is required)</div>
          <InputTextarea maxLength={2000} value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={4} className="masterCert__descriptionTextarea" />
        </div>
        <div className="acord20__sectionTitle">CANCELLATION</div>
        <div className="masterCert__disclaimer" style={{ marginBottom: 16 }}>
          Should any of the above described policies be cancelled before the expiration date thereof, notice will be delivered in accordance with the policy provisions.
        </div>

        <div className="acord29__aiHeader">
          <div className="acord20__sectionTitle" style={{ margin: 0 }}>ADDITIONAL INTEREST</div>
          <span className="masterCert__contactLabel">LOAN NUMBER</span>
          <InputText maxLength={30} value={loanNumber} onChange={(e) => setLoanNumber(e.target.value)} className="masterCert__input" style={{ flex: 1 }} />
        </div>

        <div className="acord29__aiGrid">
          <div>
            <InputText maxLength={100} value={aiName} onChange={(e) => setAiName(e.target.value)} placeholder="Name" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" />
            <InputText maxLength={100} value={aiAddress1} onChange={(e) => setAiAddress1(e.target.value)} placeholder="Address" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" />
            <InputText maxLength={100} value={aiAddress2} onChange={(e) => setAiAddress2(e.target.value)} placeholder="Address line 2" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" />
            <InputText className="masterCert__input masterCert__inputFull masterCert__inputMbSm" placeholder="Address line 3" />
            <div className="masterCert__addressGrid">
              <InputText maxLength={100} value={aiCity} onChange={(e) => setAiCity(e.target.value)} placeholder="City" className="masterCert__input masterCert__inputFull" />
              <InputText maxLength={50} value={aiState} onChange={(e) => setAiState(e.target.value)} placeholder="State" className="masterCert__input masterCert__inputFull" />
              <InputText maxLength={20} value={aiZip} onChange={(e) => setAiZip(e.target.value)} placeholder="Zip" className="masterCert__input masterCert__inputFull" />
            </div>
          </div>

          <div>
            <div className="acord29__aiCheckGrid">
              {chk('a29AiAddlIns', 'ADDITIONAL INSURED', aiAdditionalInsured, setAiAdditionalInsured)}
              {chk('a29AiMortgagee', 'MORTGAGEE', aiMortgagee, setAiMortgagee)}
              {chk('a29AiLendersLoss', "LENDER'S LOSS PAYABLE", aiLendersLossPayable, setAiLendersLossPayable)}
              {chk('a29AiUnitOwner', "UNIT-OWNER'S MORTGAGEE", aiUnitOwnerMortgagee, setAiUnitOwnerMortgagee)}
              {chk('a29AiLossPayee', 'LOSS PAYEE', aiLossPayee, setAiLossPayee)}
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Checkbox checked={false} />
                <InputText className="masterCert__input" style={{ width: '100%' }} />
              </div>
            </div>

            <div className="acord29__namedOnPolicy">
              <div className="masterCert__contactLabel" style={{ marginBottom: 6 }}>NAMED ON POLICY <span style={{ fontWeight: 400, fontStyle: 'italic' }}>(Check all that apply)</span></div>
              <div style={{ display: 'flex', gap: 16 }}>
                {chk('a29NpPrimary', 'PRIMARY', namedPrimary, setNamedPrimary)}
                {chk('a29NpExcess1', 'EXCESS POLICY 1', namedExcess1, setNamedExcess1)}
                {chk('a29NpExcess2', 'EXCESS POLICY 2', namedExcess2, setNamedExcess2)}
              </div>
            </div>

            <div style={{ marginTop: 12 }}>
              <div className="masterCert__authRepLabel">AUTHORIZED REPRESENTATIVE</div>
              <div className="masterCert__authRepRow">
                <Dropdown value={authRepSignature} onChange={(e) => setAuthRepSignature(e.value)} options={signatureOptions} optionLabel="label" optionValue="value" placeholder="Select signature:" className="masterCert__input masterCert__authRepDropdown" emptyMessage="No available options" />
                <Button label="Create New" className="p-button-outlined carrierDialog__secondaryBtn" onClick={() => setShowAddSignature(true)} />
              </div>
              {signaturePreviewUrl && (
                <div style={{ marginTop: 8 }}>
                  <img src={signaturePreviewUrl} alt="Signature preview" style={{ maxHeight: 60, maxWidth: 280, objectFit: 'contain', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 8px', background: '#fff' }} />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="masterCert__acordFooter">
          <div>ACORD 29 (2016/03)</div>
          <div>&copy; 2010-2015 ACORD CORPORATION. All rights reserved.</div>
        </div>
      </div>

      <CreateNewCarriers open={showCreateCarrier} onClose={() => setShowCreateCarrier(false)} />
      <AddNewSignature open={showAddSignature} onClose={() => setShowAddSignature(false)} onSaved={refetchSignatures} />
    </>
  );
}
